import * as React from "react";
import {Grid, Col, Row} from "react-bootstrap";
import { useIntl } from "react-intl";
import { Routine } from "redux-saga-routines";
import { connect } from "react-redux";
import {Action, ActionFunctionAny} from "redux-actions";

import SafemateAlarm from "../../Elements/Icon/Icons/DeviceIcons/SafemateAlarm";
import Card, {AlarmHandlingCard, Container, Header, WifiLocationCard} from "./card";
import Donut from "./donut";
import { initialize, fetchAlarms, fetchAhpReport, fetchReport, fetchAlarmHandleReport } from "./Store/routines";
import {Status, DeviceHardware, AhpReport, GrafanaPanel, UserTagCount, FourG, Wifi} from "./Store/types";
import { getStatusLink } from "./utils";
import { StatusEnum } from "./StatusEnum";
import { AppState } from "@Safemate/Store/types";
import { FilterAlarm, filterAlarm, ToggleWifiFilter, toggleWifiFilter } from "@Safemate/DeviceList/Store/actions";
import { AlarmFilterEnum } from "@Safemate/DeviceList/AlarmFilterEnum";
import {ICustomerWithPolicy} from "@Safemate/Model/Customer";
import {isAdmin} from "@Safemate/utils";
import { SiteTypeEnum } from "@Safemate/SiteTypeEnum";
import AlarmOperator from "@Elements/Icon/Icons/menu/AlarmOperator";
import Person from "@Elements/Icon/Icons/menu/Person";
import SubUnits from "@Elements/Icon/Icons/menu/SubUnits";
import { UserTags } from "@Safemate/enums/UserTags";
import { Checkbox } from "@Elements/CheckboxFormik";
import HeadlineAlarm from "@Elements/Icon/Icons/EventSetup/HeadlineAlarm";


interface DashboardProps{
  init: Routine<ActionFunctionAny<Action<any>>>;
  fetchAlarms: Routine<ActionFunctionAny<Action<any>>>;
  fetchAhpReport: Routine<ActionFunctionAny<Action<any>>>;
  fetchReport: Routine<ActionFunctionAny<Action<any>>>;
  fetchAlarmHandleReport: Routine<ActionFunctionAny<Action<any>>>;
  initialized: boolean;
  ahpReport: AhpReport;
  fourG: FourG;
  wifi: Wifi;
  alarms: number;
  status: Status;
  deviceHardware: DeviceHardware;
  userTag: UserTagCount;
  recipientPercentage: number;
  alarmHandledPercentage: number;
  recipientNOKPercentage: number;
  alarmHandledNOKPercentage: number;
  filterAlarm: FilterAlarm;
  toggleWifiFilter: ToggleWifiFilter;
  customersWithPolicy: ICustomerWithPolicy[];
  percentageReportInitialized: boolean;
  currentContext: SiteTypeEnum;
  grafanaPanels: GrafanaPanel[];
  hasStock: boolean;
}

export type ChartValue = {
  value: number;
  name: string;
}

export type RedirectMap = {
  [index: string]: ChartRedirect;
}

export type ChartRedirect = {
  filter: AlarmFilterEnum;
  type: number;
  userTag?: UserTags;
}

export type ChartValues = {
  values: ChartValue[];
  redirect: RedirectMap;
  filter?: AlarmFilterEnum[];
  type?: number[];
  userTag?: UserTags[];
};

const mapStateToProps = ({ dashboard: {initialization: {initialized}, report: {hasStock, wifi, fourG, userTag, ahpReport, alarms, status, deviceHardware, recipientPercentage, alarmHandledPercentage, recipientNOKPercentage, alarmHandledNOKPercentage, percentageReportInitialized, grafanaPanels}}, appData: {customersWithPolicy, currentContext}}: AppState) => {
  return {
    initialized,
    ahpReport,
    alarms,
    fourG,
    wifi,
    status,
    userTag,
    deviceHardware,
    customersWithPolicy,
    recipientPercentage,
    recipientNOKPercentage,
    alarmHandledPercentage,
    alarmHandledNOKPercentage,
    percentageReportInitialized,
    grafanaPanels,
    hasStock,
    currentContext
  }
}

const mapDispatchToProps  = {
  init: initialize,
  fetchAlarms: fetchAlarms,
  fetchReport: fetchReport,
  fetchAhpReport,
  fetchAlarmHandleReport: fetchAlarmHandleReport,
  filterAlarm,
  toggleWifiFilter
};

export default connect(mapStateToProps, mapDispatchToProps)(({init, hasStock, fetchAlarms, fetchReport, fetchAhpReport, ahpReport, alarms, wifi, fourG, status, userTag, deviceHardware, filterAlarm, toggleWifiFilter, customersWithPolicy, alarmHandledPercentage, recipientPercentage, fetchAlarmHandleReport, percentageReportInitialized, grafanaPanels, currentContext, recipientNOKPercentage, alarmHandledNOKPercentage}: DashboardProps) => {

  const { formatMessage } = useIntl();
  const adminUser = isAdmin(customersWithPolicy);

  const initRef = React.useRef("");
  const alarmIntervalRef = React.useRef(undefined);
  const reportIntervalRef = React.useRef(undefined);
  const ahpReportIntervalRef = React.useRef(undefined);
  const alarmHandleReprotIntervalRef = React.useRef(undefined);
  const [includeStock, setIncludeStock] = React.useState(false);

  React.useEffect(() => {
    setup();
    return () => {
      teardown();
    }
  }, [currentContext])

  const setup = () => {
    if(currentContext === SiteTypeEnum.AHP){
      if(!initRef.current){
        initRef.current = fetchAhpReport().type;
      }
      if(!ahpReportIntervalRef.current){
        ahpReportIntervalRef.current = setInterval(fetchAhpReport, 5*60000);
      }
    }
    else{
      if(!initRef.current){
        initRef.current = init(adminUser).type;
      }

      if(!alarmIntervalRef.current){
        alarmIntervalRef.current = setInterval(fetchAlarms, 15000);
      }
      if(!reportIntervalRef.current){
        reportIntervalRef.current = setInterval(fetchReport, 5*60000);
      }
      
      
      if( adminUser && !alarmHandleReprotIntervalRef.current){
        alarmHandleReprotIntervalRef.current = setInterval(fetchAlarmHandleReport, 5*60000);
      }
    }
  }

  const teardown = () => {
    if(currentContext === SiteTypeEnum.AHP){
      clearInterval(ahpReportIntervalRef.current);
    }
    else{
      clearInterval(alarmIntervalRef.current);
      clearInterval(reportIntervalRef.current);
      if(adminUser){
        clearInterval(alarmHandleReprotIntervalRef.current);
      }
    }
  }

  const mappedDeviceHardware = React.useMemo(() => {
    const s: ChartValues = {
      values: [],
      redirect: {},

    }
    for(const [key, value] of Object.entries(deviceHardware)){

      let displayValue = includeStock ? value[0] + value[1] : value[0];

      if(displayValue > 0){
        const name = formatMessage({id: `deviceHardware${key}`, defaultMessage: key});
        s.values.push({value: displayValue, name});
        s.redirect[name] = {
          type: parseInt(key),
          filter: AlarmFilterEnum.CLEAR
        }
      }
    }

    return s;
  }, [deviceHardware, includeStock])

  const [mappedUserTags, hasTags] = React.useMemo(() => {
    const s: ChartValues = {
      values: [],
      redirect: {},
    }
    let hasTags = false;
    for(const [key, value] of Object.entries(userTag)){

      let displayValue = includeStock ? value[0] + value[1] : value[0];

      if(key && key !== UserTags.NONE){
        hasTags = true;
      }
      if(displayValue > 0){
        const name = formatMessage({id: key, defaultMessage: key});
        s.values.push({value: displayValue, name});
        s.redirect[name] = {
          type: 0,
          userTag: key as UserTags,
          filter: AlarmFilterEnum.CLEAR
        }
      }
    }
    return [s, hasTags];
  }, [userTag, includeStock])

  const mappedStatus = React.useMemo(() => {
    const s: ChartValues = {
      values: [],
      redirect: {},
    };
    for(const [key, value] of Object.entries(status)){
      
      let displayValue = includeStock ? value[0] + value[1] : value[0];
      
      if(displayValue > 0){
        const name = formatMessage({id: key, defaultMessage: key});
        s.values.push({value: displayValue, name});
        s.redirect[name] = {
          type: 0,
          filter: getStatusLink(key as StatusEnum)
        }
      }
    }
    return s;
  }, [status, includeStock])

  const mappedFourG = React.useMemo(() => {
    const s: ChartValues = {
      values: [],
      redirect: {},
    };

    let fourGValue = includeStock ? fourG.fourG[0] + fourG.fourG[1] : fourG.fourG[0];
    let twoGValue = includeStock ? fourG.twoG[0] + fourG.twoG[1] : fourG.twoG[0];

    s.values.push({value: fourGValue, name: formatMessage({id: "fourG", defaultMessage: "4G"})});
    s.values.push({value: twoGValue, name: formatMessage({id: "twoG", defaultMessage: "2G"})});

    return s;
  }, [fourG, includeStock])

  React.useEffect(() => {
  }, [alarmHandledPercentage, recipientPercentage]);

  const alarmHandleReportContent = adminUser && percentageReportInitialized ? <React.Fragment>
    <Row style={{padding: "0px 10px"}}>
      <Col xs={12}>
        { `${formatMessage({id: "recipientPercentageLabel1", defaultMessage: "Next-of-kins exist in"})} ${recipientPercentage}% ${formatMessage({id: "recipientPercentageLabel2", defaultMessage: " of the event lists"})}` }
      </Col>
      <Col xs={12}>
        { `${formatMessage({id: "alarmHandledPercentageLabel1", defaultMessage: "Last week"})} ${alarmHandledPercentage}% ${formatMessage({id: "alarmHandledPercentageLabel2", defaultMessage: "of alarm events have been handled by next-of-kins"})}` }
      </Col>
    </Row>
  </React.Fragment>: <React.Fragment></React.Fragment>;

  if(currentContext === SiteTypeEnum.AHP){
    return(
      <Grid fluid>
        <Row style={{marginBottom: "20px"}}>
          <Col md={4}>
            <Card
              title={formatMessage({id: "escalatedAlarms", defaultMessage: "Eskalerte alarmer"})}
              count={ahpReport.numEscalatedAlarms}
              icon={<SafemateAlarm/>}
              color="#F12638"
              onClick={() => {
                location.hash = "eventview";
              }}
            />
          </Col>
          <Col md={4}>
            <Card
              title={formatMessage({id: "nonEscalatedAlarms", defaultMessage: "Ikke-eskalerte alarmer"})}
              count={ahpReport.numNonEscalatedAlarms}
              icon={<SafemateAlarm/>}
              color="#f5a623"
              onClick={() => {
                filterAlarm([AlarmFilterEnum.ALARM])
                location.hash = 'safemates';
              }}
            />
          </Col>
          <Col md={4}>
            <Card
              title={formatMessage({id: "onlineOperators", defaultMessage: "Påloggede operatører"})}
              count={ahpReport.numOnlineOperators}
              icon={<AlarmOperator/>}
              color="#26f16a"
              onClick={() => {
                location.hash = 'users/list';
              }}
            />
          </Col>
        </Row>
        <Row>
          <Col md={4}>
            <Card
              title={formatMessage({id: "privateCustomers", defaultMessage: "Privatkunder"})}
              count={ahpReport.numPrivateCustomers}
              icon={<Person/>}
              color="#26f16a"
            />
          </Col>
          <Col md={4}>
            <Card
              title={formatMessage({id: "municipalities", defaultMessage: "Kommune-kunder"})}
              count={ahpReport.numMunicipalities}
              icon={<SubUnits/>}
              color="#26f16a"
            />
          </Col>
          <Col md={4}>
            <Card
              title={formatMessage({id: "totalDevices", defaultMessage: "Enheter totalt"})}
              count={ahpReport.numTotalDevices}
              icon={<SafemateAlarm/>}
              color="#26f16a"
              onClick={() => {
                filterAlarm([AlarmFilterEnum.CLEAR])
                location.hash = 'safemates';
              }}
            />
          </Col>
        </Row>
      </Grid>
    )
  }

  const adminAlarmReportFlag = adminUser && percentageReportInitialized;

  const displayWifiCard = wifi.wifiDevicesTotal[0];

  const size = adminAlarmReportFlag && displayWifiCard ? 4 : adminAlarmReportFlag && !displayWifiCard ? 6 : !adminAlarmReportFlag && displayWifiCard ? 6 : !adminAlarmReportFlag && !displayWifiCard ? 6 : 12;

  return(
    <Grid fluid>
      <Row>
      <Col xs={12} sm={12} md={size}>
        <Card
          title={formatMessage({id: "dashAlarmHead", defaultMessage: "Pågående alarmer"})}
          count={alarms}
          icon={<HeadlineAlarm/>}
          color="#F12638"
          onClick={() => {
            filterAlarm([AlarmFilterEnum.ALARM])
            location.hash = 'safemates';
          }}
        />
      </Col>
      {
        adminAlarmReportFlag &&
          <Col xs={12} sm={12} md={size}>
            <AlarmHandlingCard
              title={formatMessage({id: "dashAlarmHandlingHead", defaultMessage: "Alarm handling"})}
              recipientPercentage={recipientNOKPercentage}
              recipientPercentageContent={formatMessage({id: "recipientNOKPercentageLabel", defaultMessage: "Next-of-kins exist in the event set up list"})}
              alarmHandledPercentage={alarmHandledNOKPercentage}
              alarmHandledPercentageContent={formatMessage({id: "alarmHandledNOKPercentageLabel", defaultMessage: "Alarm handled by next-of-kins"})}
            />
          </Col>
      }
      {displayWifiCard > 0 && <Col xs={12} sm={12} md={size}>
        <WifiLocationCard
          onClick={() => {
            toggleWifiFilter(true);
            location.hash = 'safemates';
          }}
          includeStock={includeStock}
          wifi={wifi}
        />
      </Col>}
      { adminUser && adminAlarmReportFlag && grafanaPanels && grafanaPanels.map((grafanaPanel: GrafanaPanel) => {
        return (
          <Col xs={12} sm={12} md={size}>
            <Container style={{paddingBottom: "0px"}}>
              <Header>{formatMessage({id: grafanaPanel.title, defaultMessage: grafanaPanel.name})}</Header>
              { grafanaPanel.description && <span>
                {formatMessage({id: grafanaPanel.description, defaultMessage: grafanaPanel.name})}
              </span> }
              <img src={`/ajax/grafana/panel/${grafanaPanel.id}`} width={`100%`} />
            </Container>
          </Col>
        )
      }) }
      </Row>
      {hasStock &&
        <Row style={{paddingTop: "10px"}}>
          <Col md={12}>
            <Checkbox
              id="include-stock"
              value={includeStock}
              label={formatMessage({id: "includeStock", defaultMessage: "Inkluder lageravdelinger"})}
              onChange={(event: React.SyntheticEvent<HTMLInputElement>) => {
                setIncludeStock(!includeStock);
              }}
            />
          </Col>
        </Row>}
      <Row style={{paddingTop: "20px"}}>
        <Col md={12}>
        <span style={{fontWeight: "400", fontSize: "30px"}}>{formatMessage({id: "dashOverview", defaultMessage: "Innsikt og nøkkeltall fra din organisasjon"})}:</span>
        </Col>
      </Row>
      <Row>
      <Col md={6} sm={12}>
        <Donut
          title={formatMessage({id: "dashStatusHead", defaultMessage: "Statusoversikt"})}
          values={mappedStatus}
          customScale={1.2}
        />
      </Col>
      <Col md={6} sm={12}>
        <Donut
          title={formatMessage({id: "dashDeviceTypeHead", defaultMessage: "Enhetoversikt"})}
          values={mappedDeviceHardware}
          customScale={1.2}
        />
      </Col>
      <Col md={hasTags ? 6 : 12} sm={12}>
        <Donut
          title={formatMessage({id: "fourGHead", defaultMessage: "2G vs 4G enheter"})}
          values={mappedFourG}
          customScale={1.2}
        />
        </Col>
      {hasTags && <Col md={6} sm={12}>
        <Donut
          title={formatMessage({id: "userTagsHead", defaultMessage: "Brukerkategorier"})}
          values={mappedUserTags}
          customScale={1.2}
        />
        </Col>}
      </Row>
    </Grid>
  )
})
